import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { PageTitle } from '@/components/ui/PageTitle';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Tooltip,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import {
	ArrowLeft,
	Building2,
	ChevronDown,
	Download,
	Info,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
	PolarAngleAxis,
	PolarGrid,
	PolarRadiusAxis,
	Radar,
	RadarChart,
	ResponsiveContainer,
} from 'recharts';
import { useGetCompany } from '@/services/queries/company';
import {
	useGetFormAnalytics,
	useGetForms,
	isFormAnalyticsPayload,
} from '@/services/queries/form';
import { useGetCompanyDepartments } from '@/services/queries/company-department';
import { SelectionRowTrigger } from './components/company-dashboard/SelectionRowTrigger';
import { DashboardDepartmentPickerModal } from './components/company-dashboard/DashboardDepartmentPickerModal';
import { DashboardSectorPickerModal } from './components/company-dashboard/DashboardSectorPickerModal';
import {
	downloadCompanyDashboardReportPng,
	formatDatePt,
} from './components/company-dashboard/companyDashboardUtils';

const ALL_DEPARTMENT_ID = 'all' as const;
type MonthId = string;
type BaseDeptId = string;
type DeptId = BaseDeptId | typeof ALL_DEPARTMENT_ID;

/**
 * Textos e estrutura estática do painel. KPIs numéricos mudam por mês **e** setor
 * (COMPANY_DASHBOARD_KPI_BY_SCOPE), como `byDepartment` em Avaliação e coleta.
 */
const COMPANY_DASHBOARD_PAGE_DATA = {
	resumo: {
		sectionTitle: 'Resumo',
		indicatorsSectionTitle: 'Indicadores',
		participacao: {
			cardTitle: 'Participação',
			anonymityLabel: 'anonimato garantido',
		},
		ipbe: {
			cardTitle: 'IPBE',
			cardSubtitle: 'Índice Plumaa Bem-Estar',
			comingSoonLabel: 'EM BREVE',
			obfuscatedParagraphs: [
				'Indicadores agregados por dimensão organizacional e período, sem exposição de respondentes individuais.',
				'Segmentação por faixas de risco e fatores de proteção relativos ao ciclo de coleta corrente.',
				'Tendências suavizadas para leitura executiva; detalhes brutos permanecem restritos.',
			],
			chartBarPercents: [],
		},
		riskFactors: {
			title: 'Fatores de risco',
			critical: { label: 'críticos' },
			attention: { label: 'atenção' },
			ok: { label: 'ok' },
		},
		burnoutCbi: {
			title: 'Índice de esgotamento (CBI)',
			scaleMax: 100,
			scaleHint:
				'Neste índice, maior = pior: <50 risco baixo · 50–74 risco moderado · ≥75 risco alto.',
		},
	},
	perfil: {
		sectionTitle: 'Perfil psicossocial',
		visionByDomain: {
			kicker: 'Perfil psicossocial — COPSOQ II-BR',
			cardTitle: 'Visão por Domínio',
			subtitle:
				'Score médio por área (1-5): Mais alto = pior · mais baixo = melhor\nFora da linha tracejada (parte externa) = pior · Para dentro da linha = melhor',
		},
		cbi: {
			title: 'Esgotamento — CBI',
			subtitle: 'Copenhagen Burnout Inventory — 3 dimensões · 0–100 pts',
			scaleMax: 100,
			hintCompact:
				'Alto pior (risco): <50 baixo · 50–74 moderado · ≥75 alto.',
		},
	},
	mapaCalor: {
		sectionTitle: 'Mapa de Calor',
		strainBlockTitle: 'Alto pior (nota mais alta = pior)',
		resourceBlockTitle: 'Nota mais alta = melhor',
		legendFavorable: 'Verde',
		legendAttention: 'Amarelo',
		legendCritical: 'Vermelho',
	},
} as const;

/** Mapa de calor — escala 1–5; cores pelo “estresse agregado” (ver `stressEquivalent`). */
const COPSOQ_HEAT_THRESHOLDS = {
	favorableMax: 2.33,
	attentionMax: 3.66,
	/** Linha tracejada no radar = início da faixa crítica no mesmo eixo. */
	radarAttentionLine: 3.66,
} as const;

const COPSOQ_HEAT_DIMENSIONS: ReadonlyArray<{
	id: string;
	polarity: 'demand' | 'resource';
	domain: string;
}> = [];

const COPSOQ_HEAT_DIMENSION_COUNT = COPSOQ_HEAT_DIMENSIONS.length;

function hasCompleteHeatScores(scores: readonly number[]): boolean {
	return (
		COPSOQ_HEAT_DIMENSION_COUNT > 0 &&
		scores.length === COPSOQ_HEAT_DIMENSION_COUNT &&
		scores.every((v) => typeof v === 'number' && !Number.isNaN(v))
	);
}

/**
 * Dimensões do **mapa de calor** que alimentam cada donut CBI (score = média das faixas de cor).
 *
 * - **Pessoal** — sintomas, exaustão subjetiva, sono, humor, saúde percebida e conflito trabalho–família.
 * - **Trabalho** — exigências da tarefa, papéis, insegurança e clareza organizacional (ritmo, papel, previsibilidade).
 * - **Cliente** — proxy “relação com o trabalho social”: apoios, comunidade, liderança, justiça, confiança e ofensas.
 */
const CBI_HEAT_IDS_PESSOAL = [] as const;
const CBI_HEAT_IDS_TRABALHO = [] as const;
const CBI_HEAT_IDS_CLIENTE = [] as const;

function stressEquivalent(
	rawScore: number,
	polarity: 'demand' | 'resource',
): number {
	return polarity === 'demand' ? rawScore : 5 - rawScore;
}

type HeatBand = 'favorable' | 'attention' | 'critical';

type HeatCellBand = HeatBand | 'neutral';

/**
 * Cor e “faixa de risco” pela **nota exibida** (1–5), não pelo equivalente interno.
 * - Demanda: nota alta = pior → vermelho se > 3,66.
 * - Recurso: nota alta = melhor → verde se > 3,66.
 * Atenção: 2,33–3,66 nos dois casos (ambíguo / vigilância).
 */
function heatBandFromRaw(
	raw: number,
	polarity: 'demand' | 'resource',
): HeatBand {
	const { favorableMax, attentionMax } = COPSOQ_HEAT_THRESHOLDS;
	if (polarity === 'demand') {
		if (raw < favorableMax) return 'favorable';
		if (raw <= attentionMax) return 'attention';
		return 'critical';
	}
	if (raw > attentionMax) return 'favorable';
	if (raw >= favorableMax) return 'attention';
	return 'critical';
}

function copsoqHeatCellClass(band: HeatCellBand) {
	if (band === 'neutral') {
		return 'border-stone-200 bg-stone-50 text-stone-400 shadow-none';
	}
	if (band === 'favorable') {
		return 'border-emerald-200 bg-emerald-50/95 text-emerald-950 shadow-sm';
	}
	if (band === 'attention') {
		return 'border-amber-200 bg-amber-50/95 text-amber-950 shadow-sm';
	}
	return 'border-rose-300 bg-rose-50/95 text-rose-950 shadow-sm';
}

function brThreshold(n: number) {
	return n.toLocaleString('pt-BR', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

/** Faixas da legenda do mapa (1–5), alinhadas a `heatBandFromRaw`. */
function heatMapLegendBandRanges(polarity: 'demand' | 'resource') {
	const lo = COPSOQ_HEAT_THRESHOLDS.favorableMax;
	const hi = COPSOQ_HEAT_THRESHOLDS.attentionMax;
	const loL = brThreshold(lo);
	const hiL = brThreshold(hi);
	if (polarity === 'demand') {
		return {
			favorable: {
				swatchClass: 'border-emerald-200 bg-emerald-100',
				range: `< ${loL}`,
			},
			attention: {
				swatchClass: 'border-amber-200 bg-amber-100',
				range: `${loL} – ${hiL}`,
			},
			critical: {
				swatchClass: 'border-rose-300 bg-rose-100',
				range: `> ${hiL}`,
			},
		} as const;
	}
	return {
		favorable: {
			swatchClass: 'border-emerald-200 bg-emerald-100',
			range: `> ${hiL}`,
		},
		attention: {
			swatchClass: 'border-amber-200 bg-amber-100',
			range: `${loL} – ${hiL}`,
		},
		critical: {
			swatchClass: 'border-rose-300 bg-rose-100',
			range: `< ${loL}`,
		},
	} as const;
}

function heatMapRowsFromScores(scores: readonly number[]) {
	if (!hasCompleteHeatScores(scores)) {
		return COPSOQ_HEAT_DIMENSIONS.map((dim) => ({
			dim,
			raw: null as number | null,
			stressEq: 0,
			band: 'neutral' as const,
		}));
	}
	return COPSOQ_HEAT_DIMENSIONS.map((dim, i) => {
		const raw = scores[i]!;
		const stressEq = stressEquivalent(raw, dim.polarity);
		const band = heatBandFromRaw(raw, dim.polarity);
		return { dim, raw, stressEq, band };
	});
}

function riskFactorsFromHeat(scores: readonly number[]) {
	if (!hasCompleteHeatScores(scores)) {
		return {
			criticalCount: 0,
			attentionCount: 0,
			okCount: 0,
			dimensionsTotal: 0,
		};
	}
	const rows = heatMapRowsFromScores(scores);
	let criticalCount = 0;
	let attentionCount = 0;
	let okCount = 0;
	for (const r of rows) {
		if (r.band === 'critical') criticalCount += 1;
		else if (r.band === 'attention') attentionCount += 1;
		else okCount += 1;
	}
	return {
		criticalCount,
		attentionCount,
		okCount,
		dimensionsTotal: COPSOQ_HEAT_DIMENSIONS.length,
	};
}

function domainStressAverages(scores: readonly number[]) {
	if (!hasCompleteHeatScores(scores)) return {} as Record<string, number>;
	const rows = heatMapRowsFromScores(scores);
	const sums: Record<string, { s: number; n: number }> = {};
	for (const r of rows) {
		if (!sums[r.dim.domain]) sums[r.dim.domain] = { s: 0, n: 0 };
		const b = sums[r.dim.domain]!;
		b.s += r.stressEq;
		b.n += 1;
	}
	const out: Record<string, number> = {};
	Object.keys(sums).forEach((k) => {
		const { s, n } = sums[k];
		out[k] = n ? s / n : 0;
	});
	return out;
}

function radarAxesFromHeat(scores: readonly number[]) {
	if (!hasCompleteHeatScores(scores)) return [];
	const av = domainStressAverages(scores);
	return Object.entries(av).map(([domain, score]) => ({
		label: domain,
		score: Math.round(score * 100) / 100,
	}));
}

/**
 * Pontos 0–100 por célula, alinhados às **faixas de cor do mapa** (`heatBandFromRaw`),
 * não à média linear da nota — evita CBI “bom” com vários quadros amarelos/vermelhos.
 */
function cbiPointsFromHeatBand(band: HeatBand): number {
	if (band === 'favorable') return 34;
	if (band === 'attention') return 57;
	return 81;
}

/** Média dos pontos acima para um subconjunto de dimensões (ex.: Pessoal / Trabalho / Cliente). */
function cbiScoreFromGroupedHeatBands(
	ids: readonly string[],
	scores: readonly number[],
): number {
	if (!hasCompleteHeatScores(scores)) return 0;
	let sum = 0;
	let n = 0;
	for (const id of ids) {
		const i = COPSOQ_HEAT_DIMENSIONS.findIndex((d) => d.id === id);
		if (i < 0) continue;
		const dim = COPSOQ_HEAT_DIMENSIONS[i]!;
		const raw = scores[i]!;
		const band = heatBandFromRaw(raw, dim.polarity);
		sum += cbiPointsFromHeatBand(band);
		n += 1;
	}
	if (!n) return 0;
	return Math.min(100, Math.max(0, Math.round(sum / n)));
}

/** Índice CBI do resumo = mesmo conjunto do donut “Pessoal” (`CBI_HEAT_IDS_PESSOAL`). */
function burnoutIndexFromHeat(scores: readonly number[]) {
	return cbiScoreFromGroupedHeatBands(CBI_HEAT_IDS_PESSOAL, scores);
}

function burnoutStatusLabel(score: number) {
	if (score >= 75) return 'Risco Alto';
	if (score >= 50) return 'Risco Moderado';
	return 'Risco Baixo';
}

function cbiFooterFromHeat(scores: readonly number[]) {
	if (!hasCompleteHeatScores(scores)) return '—';
	const rf = riskFactorsFromHeat(scores);
	if (!rf.dimensionsTotal) return '—';
	const pct = Math.round((rf.criticalCount / rf.dimensionsTotal) * 100);
	return `${pct}% das dimensões em nível crítico (> ${COPSOQ_HEAT_THRESHOLDS.attentionMax
		.toFixed(2)
		.replace('.', ',')})`;
}

function cbiBlockHeatFooter(ids: readonly string[], scores: readonly number[]) {
	if (!hasCompleteHeatScores(scores)) return '—';
	let critical = 0;
	let attention = 0;
	let n = 0;
	for (const id of ids) {
		const i = COPSOQ_HEAT_DIMENSIONS.findIndex((d) => d.id === id);
		if (i < 0) continue;
		n += 1;
		const dim = COPSOQ_HEAT_DIMENSIONS[i]!;
		const raw = scores[i]!;
		const band = heatBandFromRaw(raw, dim.polarity);
		if (band === 'critical') critical += 1;
		else if (band === 'attention') attention += 1;
	}
	if (!n) return '—';
	const strain = critical + attention;
	return `${strain}/${n} dimensões deste bloco em atenção ou pior`;
}

function cbiDonutsFromHeat(scores: readonly number[]) {
	if (!hasCompleteHeatScores(scores)) {
		const empty = (label: string): CbiDimensionKpi => ({
			label,
			score: 0,
			badge: '—',
			badgeTone: 'low',
			ringColor: '#d6d3d1',
			footer: '—',
		});
		return [
			empty('Vida pessoal'),
			empty('Vida laboral'),
			empty('Relação com cliente'),
		];
	}
	const mk = (
		label: string,
		ids: readonly string[],
		footer: string,
	): CbiDimensionKpi => {
		const score = cbiScoreFromGroupedHeatBands(ids, scores);
		let badge: string;
		let badgeTone: 'amber' | 'danger' | 'low';
		let ringColor: string;
		if (score >= 75) {
			badge = 'Alto';
			badgeTone = 'danger';
			ringColor = '#ef4444';
		} else if (score >= 50) {
			badge = 'Moderado';
			badgeTone = 'amber';
			ringColor = '#f59e0b';
		} else {
			badge = 'Baixo';
			badgeTone = 'low';
			ringColor = '#16a34a';
		}
		return { label, score, badge, badgeTone, ringColor, footer };
	};
	return [
		mk(
			'Vida pessoal',
			CBI_HEAT_IDS_PESSOAL,
			cbiBlockHeatFooter(CBI_HEAT_IDS_PESSOAL, scores),
		),
		mk(
			'Vida laboral',
			CBI_HEAT_IDS_TRABALHO,
			cbiBlockHeatFooter(CBI_HEAT_IDS_TRABALHO, scores),
		),
		mk(
			'Relação com cliente',
			CBI_HEAT_IDS_CLIENTE,
			cbiBlockHeatFooter(CBI_HEAT_IDS_CLIENTE, scores),
		),
	];
}

type CbiDimensionKpi = {
	label: string;
	score: number;
	badge: string;
	badgeTone: 'amber' | 'danger' | 'low';
	ringColor: string;
	footer: string;
	impactTag?: 'Alto melhor' | 'Alto pior';
};

type CbiAnalyticsUiPayload = {
	burnoutSummary: {
		score: number;
		statusLabel: string;
		footerNote: string;
	};
	dimensions: CbiDimensionKpi[];
};

function clampCbiScore(rawScore: number): number {
	return Math.min(100, Math.max(0, rawScore));
}

function cbiLevelFromScore(score: number) {
	if (score >= 75) return 'Alto' as const;
	if (score >= 50) return 'Moderado' as const;
	return 'Baixo' as const;
}

function cbiVisualFromScoreAndImpact(
	score: number,
	isPositiveImpact: boolean | null | undefined,
) {
	const level = cbiLevelFromScore(score);
	const isPositive = !!isPositiveImpact;

	if (level === 'Moderado') {
		return {
			badge: level,
			badgeTone: 'amber' as const,
			ringColor: '#f59e0b',
		};
	}

	const isFavorable =
		(isPositive && level === 'Alto') || (!isPositive && level === 'Baixo');

	if (isFavorable) {
		return {
			badge: level,
			badgeTone: 'low' as const,
			ringColor: '#16a34a',
		};
	}

	return { badge: level, badgeTone: 'danger' as const, ringColor: '#ef4444' };
}

function cbiDimensionLabelFromCategoryName(name?: string | null) {
	const normalized = (name ?? '')
		.toLocaleLowerCase('pt-BR')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');
	if (normalized.includes('pessoal')) return 'Vida pessoal';
	if (normalized.includes('trabalho') || normalized.includes('laboral'))
		return 'Vida laboral';
	if (normalized.includes('cliente')) return 'Relação com cliente';
	return name?.trim() || 'Dimensão';
}

function buildCbiUiPayloadFromAnalytics(
	analytics: Forms.FormAnalytics | null | undefined,
): CbiAnalyticsUiPayload | null {
	if (!analytics) return null;
	const categories = analytics.consolidated_data?.categories ?? [];
	const summaryScore = Math.round(
		clampCbiScore(analytics.consolidated_data?.total_score ?? 0),
	);

	const dimensions = categories.map((cat) => {
		const displayScore = Math.round(clampCbiScore(cat.average ?? 0));
		const visual = cbiVisualFromScoreAndImpact(
			displayScore,
			cat.is_positive_impact,
		);
		return {
			label: cbiDimensionLabelFromCategoryName(cat.name),
			score: displayScore,
			...visual,
			footer: `${cat.scored_questions} pergunta(s) respondida(s)`,
			impactTag: cat.is_positive_impact ? 'Alto melhor' : 'Alto pior',
		} satisfies CbiDimensionKpi;
	});

	return {
		burnoutSummary: {
			score: summaryScore,
			statusLabel: burnoutStatusLabel(summaryScore),
			footerNote: `${analytics.answers_count} resposta(s) consolidadas`,
		},
		dimensions,
	};
}

type HeatMapSortMode = 'criticalFirst' | 'favorableFirst';

type HeatMapRow = {
	dim: {
		id: string;
		shortLabel: string;
		fullLabel: string;
		polarity: 'demand' | 'resource';
		groupLabel: string;
	};
	raw: number | null;
	stressEq: number;
	band: HeatCellBand;
};
const HEAT_MAP_SORT_LABELS: Record<HeatMapSortMode, string> = {
	criticalFirst: 'Ordenação: críticos primeiro',
	favorableFirst: 'Ordenação: favoráveis primeiro',
};

function sortHeatMapRows(
	rows: readonly HeatMapRow[],
	mode: HeatMapSortMode,
): HeatMapRow[] {
	const stable = rows.map((row, idx) => ({ row, idx }));
	const byStableIndex = (a: { idx: number }, b: { idx: number }) =>
		a.idx - b.idx;

	return stable
		.sort((a, b) => {
			const scoreA =
				mode === 'criticalFirst' ? a.row.stressEq : -a.row.stressEq;
			const scoreB =
				mode === 'criticalFirst' ? b.row.stressEq : -b.row.stressEq;
			if (scoreA !== scoreB) return scoreB - scoreA;
			const ar = a.row.raw ?? NaN;
			const br = b.row.raw ?? NaN;
			if (ar !== br) return ar - br;
			return byStableIndex(a, b);
		})
		.map((x) => x.row);
}

function buildHeatMapRowsFromAnalytics(
	analytics: Forms.FormAnalytics | null,
): HeatMapRow[] {
	if (!analytics) return [];
	const consolidated = analytics.consolidated_data;
	const categories = consolidated?.categories ?? [];
	const subcategories = consolidated?.subcategories ?? [];
	if (subcategories.length === 0) return [];
	const categoryNameById = new Map(
		categories.map((c) => [c.category_id, c.name]),
	);
	const source = subcategories.map((s) => ({
		id: s.subcategory_id,
		name: s.name,
		average: s.average,
		is_positive_impact: s.is_positive_impact,
		groupLabel: categoryNameById.get(s.category_id) ?? 'Categoria',
	}));

	return source.map((item) => {
		const raw = item.average;
		const polarity: 'demand' | 'resource' = item.is_positive_impact
			? 'resource'
			: 'demand';
		if (raw == null || Number.isNaN(raw)) {
			return {
				dim: {
					id: item.id,
					shortLabel: item.name,
					fullLabel: item.name,
					polarity,
					groupLabel: item.groupLabel,
				},
				raw: null,
				stressEq: 0,
				band: 'neutral',
			};
		}
		return {
			dim: {
				id: item.id,
				shortLabel: item.name,
				fullLabel: item.name,
				polarity,
				groupLabel: item.groupLabel,
			},
			raw,
			stressEq: stressEquivalent(raw, polarity),
			band: heatBandFromRaw(raw, polarity),
		};
	});
}

type MonthDashboardKpis = {
	hasHeatScoresData: boolean;
	riskFactors: {
		criticalCount: number;
		attentionCount: number;
		okCount: number;
		dimensionsTotal: number;
	};
	burnoutCbi: {
		score: number;
		statusLabel: string;
		footerNote: string;
	};
	visionByDomain: {
		legendSolid: string;
		legendOutline: string;
		attentionZoneScore: number;
		axes: readonly { label: string; score: number }[];
		footerLead: string;
		footerHighlight1: string;
		footerMiddle: string;
		footerHighlight2: string;
		footerTail: string;
	};
	cbiDimensions: readonly CbiDimensionKpi[];
	heatMapCells: ReturnType<typeof heatMapRowsFromScores>;
};

function visionFooterFromDomains(scores: readonly number[], scopeTag: string) {
	if (!hasCompleteHeatScores(scores)) {
		return {
			footerLead: '',
			footerHighlight1: '',
			footerMiddle: '',
			footerHighlight2: '',
			footerTail: '',
		};
	}
	const av = domainStressAverages(scores);
	const ranked = Object.entries(av)
		.sort((a, b) => b[1] - a[1])
		.map(([domain]) => domain);
	const first = ranked[0] ?? '—';
	const second = ranked[1] ?? '—';
	return {
		footerLead:
			'Domínios mais afastados do centro = maior carga psicossocial agregada. Destaque:',
		footerHighlight1: first,
		footerMiddle: 'e',
		footerHighlight2: second,
		footerTail: ` — recorte ${scopeTag}.`,
	};
}

function buildMonthDashboardKpisFromHeat(
	scores: readonly number[],
	legendPrefix: string,
): MonthDashboardKpis {
	const heatOk = hasCompleteHeatScores(scores);
	const rf = riskFactorsFromHeat(scores);
	const bScore = burnoutIndexFromHeat(scores);
	const axes = radarAxesFromHeat(scores);
	const vf = visionFooterFromDomains(scores, legendPrefix);
	return {
		hasHeatScoresData: heatOk,
		riskFactors: rf,
		burnoutCbi: {
			score: bScore,
			statusLabel: heatOk ? burnoutStatusLabel(bScore) : '—',
			footerNote: cbiFooterFromHeat(scores),
		},
		visionByDomain: {
			legendSolid: legendPrefix,
			legendOutline: `Limiar atenção (${COPSOQ_HEAT_THRESHOLDS.attentionMax})`,
			attentionZoneScore: COPSOQ_HEAT_THRESHOLDS.radarAttentionLine,
			axes,
			footerLead: vf.footerLead,
			footerHighlight1: vf.footerHighlight1,
			footerMiddle: vf.footerMiddle,
			footerHighlight2: vf.footerHighlight2,
			footerTail: vf.footerTail,
		},
		cbiDimensions: cbiDonutsFromHeat(scores),
		heatMapCells: heatMapRowsFromScores(scores),
	};
}

/** Só as notas 1–5 por dimensão (ordem = COPSOQ_HEAT_DIMENSIONS). Demais blocos são derivados. */
const COMPANY_DASHBOARD_HEAT_SCORES_BY_SCOPE: Record<
	MonthId,
	Record<BaseDeptId, readonly number[]>
> = {};

function getDashboardKpis(
	monthId: MonthId,
	departmentId: DeptId,
	legendPrefix: string,
): MonthDashboardKpis {
	const monthBucket = (COMPANY_DASHBOARD_HEAT_SCORES_BY_SCOPE[monthId] ??
		{}) as Record<string, readonly number[]>;
	let scores: readonly number[] = Object.values(monthBucket)[0] ?? [];

	if (departmentId === ALL_DEPARTMENT_ID) {
		const allScores = Object.values(monthBucket);
		const firstLen = allScores[0]?.length ?? 0;
		if (allScores.length > 0 && firstLen > 0) {
			scores = Array.from({ length: firstLen }, (_, idx) => {
				const sum = allScores.reduce(
					(acc, arr) => acc + (arr[idx] ?? 0),
					0,
				);
				return sum / allScores.length;
			});
		}
	} else {
		scores = monthBucket[departmentId] ?? scores;
	}

	return buildMonthDashboardKpisFromHeat(scores, legendPrefix);
}
void getDashboardKpis;

const labelClass =
	'text-[10px] font-black uppercase tracking-widest text-stone-400';

const selectTriggerClass =
	'h-9 w-full rounded-lg border-stone-200 bg-stone-50 px-2.5 text-xs font-bold text-stone-600 focus:border-stone-400 focus:ring-0';

const DONUT = {
	r: 40,
	stroke: 10,
	progressColor: '#e11d74',
} as const;

function ParticipationDonut({ percent }: { percent: number }) {
	const c = 2 * Math.PI * DONUT.r;
	const dash = (Math.min(100, Math.max(0, percent)) / 100) * c;
	const display = Math.round(percent);

	return (
		<div
			className="relative h-[7.5rem] w-[7.5rem] shrink-0"
			aria-label={`Participação ${display}%`}
		>
			<svg
				className="h-full w-full -rotate-90"
				viewBox="0 0 100 100"
				aria-hidden
			>
				<circle
					cx="50"
					cy="50"
					r={DONUT.r}
					fill="none"
					className="stroke-stone-200"
					strokeWidth={DONUT.stroke}
				/>
				<circle
					cx="50"
					cy="50"
					r={DONUT.r}
					fill="none"
					stroke={DONUT.progressColor}
					strokeWidth={DONUT.stroke}
					strokeLinecap="round"
					strokeDasharray={`${dash} ${c}`}
				/>
			</svg>
			<div className="absolute inset-0 flex items-center justify-center">
				<span className="text-xl font-black tracking-tight text-stone-900">
					{display}%
				</span>
			</div>
		</div>
	);
}

const COPSOQ_ACCENT = '#e11d74';

function cbiBadgeClass(tone: 'amber' | 'danger' | 'low') {
	if (tone === 'amber') return 'bg-amber-100 text-amber-800';
	if (tone === 'danger') return 'bg-red-100 text-red-700';
	return 'bg-emerald-100 text-emerald-800';
}

function cbiSummaryToneFromScore(
	score?: number | null,
): 'danger' | 'amber' | 'low' {
	if (score == null || Number.isNaN(score)) return 'amber';
	if (score >= 75) return 'danger';
	if (score >= 50) return 'amber';
	return 'low';
}

function cbiSummaryValueClass(tone: 'danger' | 'amber' | 'low') {
	if (tone === 'danger') return 'text-red-500';
	if (tone === 'amber') return 'text-amber-500';
	return 'text-emerald-600';
}

function cbiSummaryBarClass(tone: 'danger' | 'amber' | 'low') {
	if (tone === 'danger') return 'bg-red-500';
	if (tone === 'amber') return 'bg-amber-500';
	return 'bg-emerald-500';
}

function CbiScoreDonut({
	score,
	max,
	ringColor,
}: {
	score: number;
	max: number;
	ringColor: string;
}) {
	/* Raio maior no viewBox afasta a borda interna do anel do texto central (nº + /100). */
	const r = 40;
	const stroke = 6;
	const c = 2 * Math.PI * r;
	const pct = Math.min(100, Math.max(0, (score / max) * 100));
	const dash = (pct / 100) * c;

	return (
		<div
			className="relative h-24 w-24 shrink-0 md:h-[6.25rem] md:w-[6.25rem] [&_svg]:outline-none"
			aria-label={`${score} de ${max}`}
			onMouseDown={(e) => e.preventDefault()}
		>
			<svg
				className="h-full w-full -rotate-90 outline-none focus:outline-none"
				viewBox="0 0 100 100"
				aria-hidden
			>
				<circle
					cx="50"
					cy="50"
					r={r}
					fill="none"
					className="stroke-stone-200"
					strokeWidth={stroke}
				/>
				<circle
					cx="50"
					cy="50"
					r={r}
					fill="none"
					stroke={ringColor}
					strokeWidth={stroke}
					strokeLinecap="round"
					strokeDasharray={`${dash} ${c}`}
				/>
			</svg>
			<div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
				<span
					className="text-2xl font-black tracking-tight md:text-[1.65rem]"
					style={{ color: ringColor }}
				>
					{score}
				</span>
				<span className="mt-0.5 text-[10px] font-bold text-stone-400">
					/{max}
				</span>
			</div>
		</div>
	);
}

const CompanyDashboardPage = () => {
	const { id: companyId } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const { data: companyDetail } = useGetCompany(companyId ?? '', {
		enabled: !!companyId,
	});

	const { data: formsNr1, isLoading: loadingNr1 } = useGetForms(
		{ companyId, type: 'EMPLOYEE_NR1', limit: 100 },
		{ enabled: !!companyId },
	);
	const { data: formsCbi, isLoading: loadingCbi } = useGetForms(
		{ companyId, type: 'CBI', limit: 100 },
		{ enabled: !!companyId },
	);

	const formsNr1List = formsNr1?.data ?? [];
	const formsCbiList = formsCbi?.data ?? [];

	const monthIdFromDate = useCallback((dateInput?: string | null) => {
		if (!dateInput) return null;
		const date = new Date(dateInput);
		if (Number.isNaN(date.getTime())) return null;
		return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
			2,
			'0',
		)}`;
	}, []);

	const monthLabelFromMonthId = useCallback((monthId: string) => {
		const [year, month] = monthId.split('-');
		const asDate = new Date(Number(year), Number(month) - 1, 1);
		return asDate.toLocaleDateString('pt-BR', {
			month: 'long',
			year: 'numeric',
		});
	}, []);

	const commonMonthOptions = useMemo(() => {
		const bucketNr1 = new Map<
			string,
			{ id: string; created_at?: string | null }[]
		>();
		const bucketCbi = new Map<
			string,
			{ id: string; created_at?: string | null }[]
		>();

		for (const form of formsNr1List) {
			const monthId = monthIdFromDate(form.created_at);
			if (!monthId) continue;
			const arr = bucketNr1.get(monthId) ?? [];
			arr.push({ id: form.id, created_at: form.created_at });
			bucketNr1.set(monthId, arr);
		}

		for (const form of formsCbiList) {
			const monthId = monthIdFromDate(form.created_at);
			if (!monthId) continue;
			const arr = bucketCbi.get(monthId) ?? [];
			arr.push({ id: form.id, created_at: form.created_at });
			bucketCbi.set(monthId, arr);
		}

		const sharedMonthIds = [...bucketNr1.keys()]
			.filter((monthId) => bucketCbi.has(monthId))
			.sort((a, b) => b.localeCompare(a));

		return sharedMonthIds.map((monthId) => {
			const nr1Latest = (bucketNr1.get(monthId) ?? []).sort((a, b) =>
				(b.created_at ?? '').localeCompare(a.created_at ?? ''),
			)[0];
			const cbiLatest = (bucketCbi.get(monthId) ?? []).sort((a, b) =>
				(b.created_at ?? '').localeCompare(a.created_at ?? ''),
			)[0];

			return {
				monthId,
				label: monthLabelFromMonthId(monthId),
				nr1FormId: nr1Latest?.id ?? null,
				cbiFormId: cbiLatest?.id ?? null,
				nr1CreatedAt: nr1Latest?.created_at ?? null,
				cbiCreatedAt: cbiLatest?.created_at ?? null,
			};
		});
	}, [formsNr1List, formsCbiList, monthIdFromDate, monthLabelFromMonthId]);

	const [selectedMonthId, setSelectedMonthId] = useState<MonthId | null>(
		null,
	);

	useEffect(() => {
		if (commonMonthOptions.length === 0) {
			setSelectedMonthId(null);
			return;
		}
		setSelectedMonthId((prev) =>
			prev && commonMonthOptions.some((m) => m.monthId === prev)
				? prev
				: commonMonthOptions[0]!.monthId,
		);
	}, [commonMonthOptions]);

	const selectedMonthOption = useMemo(
		() =>
			selectedMonthId
				? commonMonthOptions.find(
						(m) => m.monthId === selectedMonthId,
				  ) ?? null
				: null,
		[commonMonthOptions, selectedMonthId],
	);

	const selectedFormIdNr1 = selectedMonthOption?.nr1FormId ?? null;
	const selectedFormIdCbi = selectedMonthOption?.cbiFormId ?? null;

	const { data: deptRes, isLoading: loadingDepts } = useGetCompanyDepartments(
		{ page: 1, limit: 100, company_id: companyId },
		{ enabled: !!companyId },
	);
	const departmentsList = deptRes?.data ?? [];

	const [departmentId, setDepartmentId] = useState<DeptId>(ALL_DEPARTMENT_ID);
	/** null = todo o departamento na API (só `departmentId`); string = setor específico */
	const [sectorId, setSectorId] = useState<string | null>(null);

	useEffect(() => {
		setSectorId(null);
	}, [departmentId]);

	const [openDeptModal, setOpenDeptModal] = useState(false);
	const [openSectorModal, setOpenSectorModal] = useState(false);

	const analyticsBaseParams =
		useMemo((): Forms.FindFormAnalyticsQueryParams => {
			const base: Forms.FindFormAnalyticsQueryParams = {
				companyId: companyId ?? '',
			};
			if (departmentId !== ALL_DEPARTMENT_ID) {
				base.departmentId = departmentId;
			}
			if (sectorId && departmentId !== ALL_DEPARTMENT_ID) {
				base.sectorId = sectorId;
			}
			return base;
		}, [companyId, departmentId, sectorId]);

	const analyticsParamsNr1 = useMemo(
		() => analyticsBaseParams,
		[analyticsBaseParams],
	);

	const analyticsParamsCbi = useMemo(
		() => analyticsBaseParams,
		[analyticsBaseParams],
	);

	const analyticsEnabledNr1 = !!companyId && !!selectedFormIdNr1;

	const analyticsEnabledCbi = !!companyId && !!selectedFormIdCbi;

	const { data: analyticsResNr1 } = useGetFormAnalytics(
		selectedFormIdNr1 ?? '',
		analyticsParamsNr1,
		{ enabled: analyticsEnabledNr1 },
	);

	const { data: analyticsResCbi } = useGetFormAnalytics(
		selectedFormIdCbi ?? '',
		analyticsParamsCbi,
		{ enabled: analyticsEnabledCbi },
	);

	const analyticsPayloadNr1Valid =
		analyticsResNr1 && isFormAnalyticsPayload(analyticsResNr1)
			? analyticsResNr1
			: null;
	const analyticsPayloadNr1 =
		analyticsResNr1 && isFormAnalyticsPayload(analyticsResNr1)
			? analyticsResNr1
			: null;
	const analyticsPayloadCbi =
		analyticsResCbi && isFormAnalyticsPayload(analyticsResCbi)
			? analyticsResCbi
			: null;
	const cbiUiPayload = useMemo(
		() => buildCbiUiPayloadFromAnalytics(analyticsPayloadCbi),
		[analyticsPayloadCbi],
	);

	const selectedDeptEntity = departmentsList.find(
		(d) => d.id === departmentId,
	);
	const sectorsForDept = selectedDeptEntity?.company_sector ?? [];

	const activeDeptLabel = useMemo(() => {
		if (departmentId === ALL_DEPARTMENT_ID) return 'Todos';
		return selectedDeptEntity?.name ?? '—';
	}, [departmentId, selectedDeptEntity?.name]);

	const periodLegend = useMemo(() => {
		if (!selectedMonthOption?.label) return '—';
		return selectedMonthOption.label;
	}, [selectedMonthOption]);

	const companyDisplayName = useMemo(
		() =>
			companyDetail?.trade_name?.trim() ||
			companyDetail?.legal_name?.trim() ||
			companyId ||
			'—',
		[companyDetail?.legal_name, companyDetail?.trade_name, companyId],
	);

	const burnoutCbiSummary = cbiUiPayload?.burnoutSummary ?? null;
	const burnoutCbiTone = cbiSummaryToneFromScore(burnoutCbiSummary?.score);
	const cbiDimensions = cbiUiPayload?.dimensions ?? [];

	const heatLegendDemand = heatMapLegendBandRanges('demand');
	const heatLegendResource = heatMapLegendBandRanges('resource');
	const heatMapRows = useMemo(
		() => buildHeatMapRowsFromAnalytics(analyticsPayloadNr1),
		[analyticsPayloadNr1],
	);
	const nr1SubcategoryCount =
		analyticsPayloadNr1?.consolidated_data?.subcategories?.length ?? 0;
	const hasDemandCategory =
		(analyticsPayloadNr1?.consolidated_data?.subcategories ?? []).some(
			(c) => !c.is_positive_impact,
		) || heatMapRows.some((r) => r.dim.polarity === 'demand');
	const hasResourceCategory =
		(analyticsPayloadNr1?.consolidated_data?.subcategories ?? []).some(
			(c) => c.is_positive_impact,
		) || heatMapRows.some((r) => r.dim.polarity === 'resource');
	const mapSectionTitle = `${
		COMPANY_DASHBOARD_PAGE_DATA.mapaCalor.sectionTitle
	}${
		nr1SubcategoryCount > 0 ? ` — ${nr1SubcategoryCount} subcategorias` : ''
	}`;
	const visionByDomainCategories =
		analyticsPayloadNr1?.consolidated_data?.categories ?? [];
	const hasVisionByDomainData = visionByDomainCategories.length > 0;
	const visionByDomainAttentionScore =
		COPSOQ_HEAT_THRESHOLDS.radarAttentionLine;
	const visionByDomainLegendSolid = `${periodLegend} · ${activeDeptLabel}`;
	const visionByDomainLegendOutline = `Limiar atenção (${visionByDomainAttentionScore})`;
	const riskFactorsFromMap = useMemo(() => {
		const validRows = heatMapRows.filter((r) => r.raw != null);
		let criticalCount = 0;
		let attentionCount = 0;
		let okCount = 0;
		for (const row of validRows) {
			if (row.band === 'critical') criticalCount += 1;
			else if (row.band === 'attention') attentionCount += 1;
			else if (row.band === 'favorable') okCount += 1;
		}
		return {
			hasData: validRows.length > 0,
			criticalCount,
			attentionCount,
			okCount,
			dimensionsTotal: validRows.length,
		};
	}, [heatMapRows]);

	const hasSelectedAnalyticsForm = !!selectedFormIdNr1;
	const collectionByDept = useMemo(() => {
		if (!hasSelectedAnalyticsForm) {
			return {
				responseCount: null as number | null,
				participationPercent: null as number | null,
				pending: null as number | null,
				participationTotal: null as number | null,
			};
		}
		const answers = analyticsPayloadNr1Valid?.answers_count ?? 0;
		const pending = analyticsPayloadNr1Valid?.pending_response_count ?? 0;
		/** Total no recorte = colaboradores com resposta + ainda sem resposta (denominador do %). */
		const participationTotal = answers + pending;
		const participationPercent =
			participationTotal > 0
				? Math.round((answers / participationTotal) * 100)
				: 0;
		return {
			responseCount: answers,
			participationPercent,
			pending,
			participationTotal,
		};
	}, [analyticsPayloadNr1Valid, hasSelectedAnalyticsForm]);

	const sectorTriggerLabel = useMemo(() => {
		if (departmentId === ALL_DEPARTMENT_ID) return '';
		if (sectorId === null) return 'Todo o departamento';
		const s = sectorsForDept.find((x) => x.id === sectorId);
		return s?.name ?? '';
	}, [departmentId, sectorId, sectorsForDept]);

	const coletaStartEnd = useMemo(() => {
		if (!selectedMonthOption) {
			return { start: '—', end: '—' };
		}
		const candidates = [
			selectedMonthOption.nr1CreatedAt,
			selectedMonthOption.cbiCreatedAt,
		]
			.filter((v): v is string => !!v)
			.map((v) => new Date(v))
			.filter((d) => !Number.isNaN(d.getTime()));

		if (candidates.length === 0) {
			return { start: '—', end: '—' };
		}

		const timestamps = candidates.map((d) => d.getTime());
		const minDate = new Date(Math.min(...timestamps));
		const maxDate = new Date(Math.max(...timestamps));
		return {
			start: formatDatePt(minDate.toISOString()),
			end: formatDatePt(maxDate.toISOString()),
		};
	}, [selectedMonthOption]);

	const [isIndicadoresOpen, setIsIndicadoresOpen] = useState(true);
	const [isPerfilOpen, setIsPerfilOpen] = useState(true);
	const [strainSortMode, setStrainSortMode] =
		useState<HeatMapSortMode>('criticalFirst');
	const [resourceSortMode, setResourceSortMode] =
		useState<HeatMapSortMode>('criticalFirst');
	const [isExportingReport, setIsExportingReport] = useState(false);
	const [companyLogoLoadFailed, setCompanyLogoLoadFailed] = useState(false);
	const reportRootRef = useRef<HTMLElement | null>(null);

	const companyLogoSrc = useMemo(() => {
		if (companyLogoLoadFailed) return null;
		return companyDetail?.logo_img_url?.trim() || null;
	}, [companyDetail?.logo_img_url, companyLogoLoadFailed]);

	useEffect(() => {
		setCompanyLogoLoadFailed(false);
	}, [companyId, companyDetail?.logo_img_url]);

	const visionByDomainRadarData = useMemo(
		() =>
			visionByDomainCategories.map((c) => ({
				subject: c.name,
				avaliacao: Math.round(c.average * 100) / 100,
				zona: visionByDomainAttentionScore,
			})),
		[visionByDomainCategories, visionByDomainAttentionScore],
	);

	const strainRows = useMemo(
		() =>
			sortHeatMapRows(
				heatMapRows.filter((r) => r.dim.polarity === 'demand'),
				strainSortMode,
			),
		[heatMapRows, strainSortMode],
	);

	const resourceRows = useMemo(
		() =>
			sortHeatMapRows(
				heatMapRows.filter((r) => r.dim.polarity === 'resource'),
				resourceSortMode,
			),
		[heatMapRows, resourceSortMode],
	);

	const handleExportReport = useCallback(async () => {
		if (!reportRootRef.current || isExportingReport) return;
		const snapshot = {
			indicadores: isIndicadoresOpen,
			perfil: isPerfilOpen,
		};

		flushSync(() => {
			setIsIndicadoresOpen(true);
			setIsPerfilOpen(true);
		});

		setIsExportingReport(true);
		try {
			await downloadCompanyDashboardReportPng(reportRootRef.current);
			toast.success('Relatório baixado com sucesso.');
		} catch (error) {
			console.error('Erro ao exportar relatório:', error);
			toast.error(
				'Não foi possível baixar o relatório. Tente novamente.',
			);
		} finally {
			flushSync(() => {
				setIsIndicadoresOpen(snapshot.indicadores);
				setIsPerfilOpen(snapshot.perfil);
			});
			setIsExportingReport(false);
		}
	}, [isExportingReport, isIndicadoresOpen, isPerfilOpen]);

	return (
		<section
			ref={(node) => {
				reportRootRef.current = node;
			}}
			className="w-full animate-fade-in space-y-6 px-4 pb-14 pt-6 md:px-8 md:pb-20 md:pt-8"
		>
			<div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-300">
				<button
					type="button"
					onClick={() => navigate('/admin/empresas')}
					className="text-stone-400 transition-colors hover:text-stone-700"
				>
					Empresas
				</button>
				<span className="text-stone-200">/</span>
				<span className="truncate text-stone-900">
					Painel da empresa
				</span>
			</div>

			<div className="space-y-1">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
					<div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
						<button
							type="button"
							onClick={() => navigate(-1)}
							className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-600 shadow-sm transition-colors hover:bg-stone-50"
							title="Voltar"
							aria-label="Voltar"
						>
							<ArrowLeft className="h-4 w-4" />
						</button>

						<div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden">
							{companyLogoSrc && !isExportingReport ? (
								<img
									src={companyLogoSrc}
									alt={`Logo de ${companyDisplayName}`}
									className="h-full w-full object-cover"
									onError={() =>
										setCompanyLogoLoadFailed(true)
									}
								/>
							) : (
								<Building2
									className="h-6 w-6 text-stone-500"
									aria-hidden
								/>
							)}
						</div>

						<div className="min-w-0 flex-1">
							<PageTitle text={companyDisplayName} />
						</div>
					</div>
				</div>
			</div>

			<div className="w-full rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
				<div className="flex flex-col gap-4 sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-end">
					<div className="min-w-0">
						<p className={labelClass}>Avaliação e coleta</p>
						<p className="mt-1 text-sm font-bold leading-snug text-stone-900">
							Formulário
						</p>
					</div>
					<div className="w-full sm:w-[16rem] sm:justify-self-center sm:text-center">
						<p className={cn(labelClass, 'mb-1')}>
							Mês de referência
						</p>
						<Select
							value={selectedMonthId ?? ''}
							onValueChange={(v) => setSelectedMonthId(v)}
							disabled={
								!companyId || commonMonthOptions.length === 0
							}
						>
							<SelectTrigger className={selectTriggerClass}>
								<SelectValue>
									{selectedMonthOption?.label ??
										(loadingNr1 || loadingCbi
											? 'Carregando meses...'
											: 'Sem mês com NR1 e CBI')}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									{commonMonthOptions.map((month) => (
										<SelectItem
											key={month.monthId}
											value={month.monthId}
										>
											{month.label}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>
					<div className="sm:justify-self-end">
						<button
							type="button"
							onClick={() => {
								void handleExportReport();
							}}
							disabled={isExportingReport}
							title="Exporta uma imagem PNG apenas com o conteúdo do relatório."
							className="inline-flex shrink-0 items-center gap-2 self-start rounded-xl bg-[#e11d74] px-2.5 py-2 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm shadow-pink-100/30 transition-colors hover:bg-pink-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-300"
						>
							<Download className="size-3.5" aria-hidden />
							{isExportingReport
								? 'Exportando...'
								: 'Baixar relatório'}
						</button>
					</div>
				</div>

				<div className="mt-4 space-y-3 border-t border-stone-100 pt-4">
					<SelectionRowTrigger
						label="Departamento"
						displayText={
							departmentId === ALL_DEPARTMENT_ID
								? 'Todos'
								: activeDeptLabel
						}
						placeholder="Selecione o departamento"
						onOpen={() => setOpenDeptModal(true)}
						disabled={!companyId}
					/>
					{departmentId !== ALL_DEPARTMENT_ID &&
					sectorsForDept.length > 0 ? (
						<SelectionRowTrigger
							label="Setor"
							displayText={sectorTriggerLabel}
							placeholder="Todo o departamento ou setor"
							onOpen={() => setOpenSectorModal(true)}
						/>
					) : null}
				</div>

				<div className="mt-4 border-t border-stone-100 pt-3">
					<p className="text-[11px] font-medium leading-relaxed text-stone-400">
						Coleta iniciada em {coletaStartEnd.start} - Coleta
						encerrada em {coletaStartEnd.end}
					</p>
				</div>
			</div>
			<DashboardDepartmentPickerModal
				isOpen={openDeptModal}
				onClose={() => setOpenDeptModal(false)}
				departments={departmentsList}
				isLoading={loadingDepts}
				selectedId={departmentId}
				onSelect={(depId) => setDepartmentId(depId as DeptId)}
			/>
			<DashboardSectorPickerModal
				isOpen={openSectorModal}
				onClose={() => setOpenSectorModal(false)}
				sectors={sectorsForDept}
				selectedSectorId={sectorId}
				onSelect={(sid) => setSectorId(sid)}
			/>

			{/* Indicadores — seção própria (participação + fatores de risco + CBI) */}
			<div className="w-full overflow-visible rounded-2xl border border-stone-100 bg-white shadow-sm">
				<div className="flex w-full items-center justify-between gap-4 px-4 py-5 md:px-6">
					<span className="text-base font-bold tracking-tight text-stone-900 md:text-lg">
						{
							COMPANY_DASHBOARD_PAGE_DATA.resumo
								.indicatorsSectionTitle
						}
					</span>
					<button
						type="button"
						onClick={() => setIsIndicadoresOpen((v) => !v)}
						className="-m-1 rounded-lg p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
						aria-expanded={isIndicadoresOpen}
						aria-label={
							isIndicadoresOpen
								? 'Recolher indicadores'
								: 'Expandir indicadores'
						}
					>
						<ChevronDown
							className={cn(
								'size-5 transition-transform duration-300',
								isIndicadoresOpen ? 'rotate-180' : 'rotate-0',
							)}
						/>
					</button>
				</div>

				<div
					className={cn(
						'overflow-hidden transition-all duration-300 ease-in-out',
						isIndicadoresOpen
							? 'max-h-[4800px] opacity-100'
							: 'max-h-0 opacity-0',
					)}
				>
					<div className="grid grid-cols-1 items-stretch gap-6 px-4 pb-6 pt-2 md:px-6 md:[grid-template-columns:6fr_13fr_13fr]">
						<div className="flex min-w-0 flex-col gap-3 rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
							<p className={cn(labelClass, 'leading-tight')}>
								{
									COMPANY_DASHBOARD_PAGE_DATA.resumo
										.participacao.cardTitle
								}
							</p>
							<div className="flex min-h-0 flex-1 items-center justify-center py-1">
								<ParticipationDonut
									percent={
										collectionByDept.participationPercent ??
										0
									}
								/>
							</div>
							{collectionByDept.responseCount != null &&
							collectionByDept.participationTotal != null ? (
								<p className="text-center text-sm font-bold tabular-nums tracking-tight text-stone-900">
									{collectionByDept.responseCount} de{' '}
									{collectionByDept.participationTotal}{' '}
									colaboradores responderam
								</p>
							) : (
								<p className="text-center text-[11px] font-medium leading-snug text-stone-400">
									Contagem de colaboradores indisponível:
									selecione mês e formulário NR1 neste recorte
									(total = quem já respondeu + pendentes).
								</p>
							)}
							<p className="text-center text-[11px] font-medium leading-snug text-stone-500">
								Percentual de participação no recorte atual.
							</p>
						</div>

						<div className="flex min-w-0 flex-col gap-2 rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
							<p
								className={cn(
									labelClass,
									'break-words leading-tight',
								)}
							>
								{
									COMPANY_DASHBOARD_PAGE_DATA.resumo
										.riskFactors.title
								}
							</p>
							<div className="flex flex-wrap items-baseline gap-2 leading-none">
								<span className="text-4xl font-black tracking-tight text-[#e11d74] md:text-5xl">
									{riskFactorsFromMap.hasData
										? riskFactorsFromMap.criticalCount
										: '—'}
								</span>
								<span className="text-lg font-bold text-stone-600 md:text-xl">
									{
										COMPANY_DASHBOARD_PAGE_DATA.resumo
											.riskFactors.critical.label
									}
								</span>
							</div>
							<div className="flex flex-wrap gap-1.5">
								<span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-bold text-rose-700">
									<span
										className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-600"
										aria-hidden
									/>
									{riskFactorsFromMap.hasData
										? riskFactorsFromMap.criticalCount
										: '—'}{' '}
									{
										COMPANY_DASHBOARD_PAGE_DATA.resumo
											.riskFactors.critical.label
									}
								</span>
								<span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-800">
									<span
										className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
										aria-hidden
									/>
									{riskFactorsFromMap.hasData
										? riskFactorsFromMap.attentionCount
										: '—'}{' '}
									{
										COMPANY_DASHBOARD_PAGE_DATA.resumo
											.riskFactors.attention.label
									}
								</span>
								<span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800">
									<span
										className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-600"
										aria-hidden
									/>
									{riskFactorsFromMap.hasData
										? riskFactorsFromMap.okCount
										: '—'}{' '}
									{
										COMPANY_DASHBOARD_PAGE_DATA.resumo
											.riskFactors.ok.label
									}
								</span>
							</div>
							<div className="flex h-2.5 w-full overflow-hidden rounded-full bg-stone-100">
								<div
									className="h-full bg-rose-600"
									style={{
										flex: riskFactorsFromMap.criticalCount,
									}}
								/>
								<div
									className="h-full bg-amber-400"
									style={{
										flex: riskFactorsFromMap.attentionCount,
									}}
								/>
								<div
									className="h-full bg-emerald-500"
									style={{
										flex: riskFactorsFromMap.okCount,
									}}
								/>
							</div>
							<p className="text-[11px] font-medium leading-snug text-stone-400">
								{riskFactorsFromMap.hasData ? (
									<>
										de {riskFactorsFromMap.dimensionsTotal}{' '}
										dimensões avaliadas
									</>
								) : (
									<>
										Sem dados consolidados para este
										recorte.
									</>
								)}
							</p>
						</div>

						<div className="flex min-w-0 flex-col gap-2 rounded-2xl border border-stone-100 bg-white p-4 shadow-sm">
							<p
								className={cn(
									labelClass,
									'break-words leading-tight',
								)}
							>
								{
									COMPANY_DASHBOARD_PAGE_DATA.resumo
										.burnoutCbi.title
								}
							</p>
							<div className="flex flex-wrap items-baseline gap-1.5 leading-none">
								<span
									className={cn(
										'text-4xl font-black tracking-tight md:text-5xl',
										cbiSummaryValueClass(burnoutCbiTone),
									)}
								>
									{burnoutCbiSummary?.score ||
									burnoutCbiSummary?.score === 0
										? burnoutCbiSummary.score
										: '—'}
								</span>
								<span className="text-lg font-bold text-stone-600 md:text-xl">
									/
									{
										COMPANY_DASHBOARD_PAGE_DATA.resumo
											.burnoutCbi.scaleMax
									}
								</span>
							</div>
							<span
								className={cn(
									'w-fit rounded-full px-2.5 py-1 text-[11px] font-bold',
									cbiBadgeClass(burnoutCbiTone),
								)}
							>
								{burnoutCbiSummary?.statusLabel ?? '—'}
							</span>
							<div className="h-2.5 w-full overflow-hidden rounded-full bg-stone-200">
								<div
									className={cn(
										'h-full rounded-full',
										cbiSummaryBarClass(burnoutCbiTone),
									)}
									style={{
										width: `${
											((burnoutCbiSummary?.score ?? 0) /
												COMPANY_DASHBOARD_PAGE_DATA
													.resumo.burnoutCbi
													.scaleMax) *
											100
										}%`,
									}}
								/>
							</div>
							<p className="text-[11px] font-medium leading-relaxed text-stone-500">
								{
									COMPANY_DASHBOARD_PAGE_DATA.resumo
										.burnoutCbi.scaleHint
								}
							</p>
							<p className="text-[11px] font-medium leading-snug text-stone-400">
								{burnoutCbiSummary?.footerNote ?? '—'}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* Perfil psicossocial — Visão por domínio + CBI */}
			<div className="w-full overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm">
				<div className="flex w-full items-center justify-between gap-3 px-4 py-4 md:px-6">
					<span className="min-w-0 text-base font-bold tracking-tight text-stone-900 md:text-lg">
						{COMPANY_DASHBOARD_PAGE_DATA.perfil.sectionTitle}
					</span>
					<button
						type="button"
						onClick={() => setIsPerfilOpen((v) => !v)}
						className="-m-1 shrink-0 rounded-lg p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
						aria-expanded={isPerfilOpen}
						aria-label={
							isPerfilOpen
								? 'Recolher perfil psicossocial'
								: 'Expandir perfil psicossocial'
						}
					>
						<ChevronDown
							className={cn(
								'size-5 transition-transform duration-300',
								isPerfilOpen ? 'rotate-180' : 'rotate-0',
							)}
						/>
					</button>
				</div>

				<div
					className={cn(
						'overflow-hidden transition-all duration-300 ease-in-out',
						isPerfilOpen
							? 'max-h-[2800px] opacity-100'
							: 'max-h-0 opacity-0',
					)}
				>
					<div className="grid grid-cols-1 items-stretch gap-4 px-4 pb-6 pt-0 md:grid-cols-2 md:gap-5 md:px-6">
						<div className="flex min-h-0 flex-col rounded-2xl border border-stone-100 bg-white p-3 shadow-sm md:p-4">
							<p className={cn(labelClass, 'leading-tight')}>
								{
									COMPANY_DASHBOARD_PAGE_DATA.perfil
										.visionByDomain.kicker
								}
							</p>
							<h3 className="pt-1 text-sm font-bold text-stone-900 md:text-base">
								{
									COMPANY_DASHBOARD_PAGE_DATA.perfil
										.visionByDomain.cardTitle
								}
							</h3>
							<p className="whitespace-pre-line text-[11px] font-medium leading-snug text-stone-500 md:text-xs">
								{
									COMPANY_DASHBOARD_PAGE_DATA.perfil
										.visionByDomain.subtitle
								}
							</p>
							<div
								className="mt-1 min-h-[220px] w-full shrink-0 md:min-h-[240px] [&_.recharts-surface:focus]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-wrapper:focus]:outline-none [&_.recharts-wrapper]:outline-none"
								onMouseDown={(e) => e.preventDefault()}
								role="presentation"
							>
								{hasVisionByDomainData ? (
									<ResponsiveContainer
										width="100%"
										height="100%"
									>
										<RadarChart
											data={visionByDomainRadarData}
											outerRadius="72%"
											innerRadius="12%"
										>
											<PolarGrid stroke="#e7e5e4" />
											<PolarAngleAxis
												dataKey="subject"
												tick={{
													fill: '#78716c',
													fontSize: 10,
												}}
											/>
											<PolarRadiusAxis
												domain={[1, 5]}
												tickCount={5}
												tick={{
													fill: '#a8a29e',
													fontSize: 9,
												}}
											/>
											<Radar
												name="avaliacao"
												dataKey="avaliacao"
												stroke={COPSOQ_ACCENT}
												fill={COPSOQ_ACCENT}
												fillOpacity={0.35}
												strokeWidth={2}
											/>
											<Radar
												name="zona"
												dataKey="zona"
												stroke={COPSOQ_ACCENT}
												fill="none"
												strokeWidth={2}
												strokeDasharray="4 3"
											/>
										</RadarChart>
									</ResponsiveContainer>
								) : (
									<div className="flex min-h-[220px] items-center justify-center rounded-xl border border-dashed border-stone-200 bg-stone-50/80 px-4 md:min-h-[240px]">
										<p className="text-center text-[11px] font-medium leading-relaxed text-stone-400">
											Sem dados consolidados para este
											recorte.
										</p>
									</div>
								)}
							</div>
							{hasVisionByDomainData ? (
								<div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] font-semibold text-stone-600">
									<span className="flex items-center gap-1.5">
										<span
											className="h-2 w-2 shrink-0 rounded-full"
											style={{
												backgroundColor: COPSOQ_ACCENT,
											}}
											aria-hidden
										/>
										{visionByDomainLegendSolid}
									</span>
									<span className="flex items-center gap-1.5">
										<span
											className="h-2 w-2 shrink-0 rounded-full border-2 border-[#e11d74] bg-transparent"
											aria-hidden
										/>
										{visionByDomainLegendOutline}
									</span>
								</div>
							) : null}
						</div>

						<div className="flex min-h-0 flex-col rounded-2xl border border-stone-100 bg-white p-4 shadow-sm md:p-5">
							<h3 className="text-sm font-bold text-stone-900 md:text-base">
								{COMPANY_DASHBOARD_PAGE_DATA.perfil.cbi.title}
							</h3>
							<p className="mt-0.5 text-[11px] font-medium leading-snug text-stone-500 md:text-xs">
								{
									COMPANY_DASHBOARD_PAGE_DATA.perfil.cbi
										.subtitle
								}
							</p>
							<p className="mt-2 text-[11px] font-medium leading-snug text-stone-600 md:text-xs">
								<span className="inline-flex rounded-full border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em] text-stone-600">
									Alto pior
								</span>{' '}
								(risco): &lt;50 baixo · 50–74 moderado · ≥75
								alto.
							</p>
							<div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
								{cbiDimensions.length > 0 ? (
									cbiDimensions.map((dim) => (
										<div
											key={dim.label}
											className="flex flex-col items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-3 text-center shadow-sm"
										>
											{dim.impactTag ? (
												<span className="rounded-full border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.04em] text-stone-600">
													{dim.impactTag}
												</span>
											) : null}
											<CbiScoreDonut
												score={dim.score}
												max={
													COMPANY_DASHBOARD_PAGE_DATA
														.perfil.cbi.scaleMax
												}
												ringColor={dim.ringColor}
											/>
											<span className="text-sm font-bold text-stone-900">
												{dim.label}
											</span>
											<span
												className={cn(
													'rounded-full px-2.5 py-1 text-[11px] font-bold',
													cbiBadgeClass(
														dim.badgeTone,
													),
												)}
											>
												{dim.badge}
											</span>
										</div>
									))
								) : (
									<div className="col-span-full rounded-xl border border-dashed border-stone-200 bg-stone-50/80 px-4 py-8 text-center text-[11px] font-medium text-stone-400">
										Selecione um formulário CBI para
										visualizar as dimensões de esgotamento.
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Mapa de Calor — seção própria */}
			<div className="w-full overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm">
				<div className="px-4 pb-2 pt-4 md:px-6 md:pt-5">
					<h2 className="text-base font-bold tracking-tight text-stone-900 md:text-lg">
						{mapSectionTitle}
					</h2>
				</div>

				<div className="px-4 pb-6 pt-0 md:px-6">
					{hasDemandCategory ? (
						<div className="mt-5 rounded-xl border border-stone-100 bg-white pb-4 pl-3 pr-3 pt-3 shadow-sm md:pl-4">
							<p className="text-xs font-bold text-stone-900">
								{
									COMPANY_DASHBOARD_PAGE_DATA.mapaCalor
										.strainBlockTitle
								}
							</p>
							<div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-5">
								{(
									[
										[
											'favorable',
											COMPANY_DASHBOARD_PAGE_DATA
												.mapaCalor.legendFavorable,
										],
										[
											'attention',
											COMPANY_DASHBOARD_PAGE_DATA
												.mapaCalor.legendAttention,
										],
										[
											'critical',
											COMPANY_DASHBOARD_PAGE_DATA
												.mapaCalor.legendCritical,
										],
									] as const
								).map(([key, name]) => (
									<span
										key={key}
										className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-stone-800"
									>
										<span
											className={cn(
												'h-2.5 w-8 shrink-0 rounded border',
												heatLegendDemand[key]
													.swatchClass,
											)}
											aria-hidden
										/>
										<span>{name}</span>
										<span className="font-semibold normal-case tabular-nums text-stone-600">
											{heatLegendDemand[key].range}
										</span>
									</span>
								))}
							</div>
							<div className="mt-3 flex items-center justify-end">
								<div className="w-full max-w-[16rem] sm:w-[16rem]">
									<Select
										value={strainSortMode}
										onValueChange={(v) =>
											setStrainSortMode(
												v as HeatMapSortMode,
											)
										}
									>
										<SelectTrigger
											className={selectTriggerClass}
										>
											<SelectValue>
												{
													HEAT_MAP_SORT_LABELS[
														strainSortMode
													]
												}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											<SelectGroup>
												<SelectItem value="criticalFirst">
													Ordenação: críticos primeiro
												</SelectItem>
												<SelectItem value="favorableFirst">
													Ordenação: favoráveis
													primeiro
												</SelectItem>
											</SelectGroup>
										</SelectContent>
									</Select>
								</div>
							</div>
							<div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
								{strainRows.map(({ dim, raw, band }) => (
									<div
										key={dim.id}
										className={cn(
											'relative flex min-h-[4.5rem] flex-col justify-between rounded-xl border p-2.5 text-left',
											copsoqHeatCellClass(band),
										)}
									>
										<span className="absolute right-2 top-2 shrink-0">
											<Tooltip
												position="top"
												tapToPinOnTouch
												content={`Esta subcategoria pertence a: ${dim.groupLabel}.`}
												className="max-w-72 whitespace-normal border border-stone-200 bg-white text-[10px] font-medium leading-snug text-stone-700 shadow-md"
											>
												<button
													type="button"
													className="rounded-full p-0.5 text-stone-500 transition-colors hover:bg-stone-200/60 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
													aria-label={`Origem da subcategoria ${dim.shortLabel}`}
												>
													<Info className="size-3" />
												</button>
											</Tooltip>
										</span>
										<span className="text-lg font-black tabular-nums leading-none">
											{raw == null
												? '—'
												: `${
														Math.round(raw * 10) /
														10
												  }/5`}
										</span>
										<span className="mt-0.5 pr-4 text-[10px] font-bold leading-tight">
											{dim.shortLabel}
										</span>
									</div>
								))}
							</div>
						</div>
					) : null}

					{hasResourceCategory ? (
						<div className="mt-5 rounded-xl border border-stone-100 bg-white pb-4 pl-3 pr-3 pt-3 shadow-sm md:pl-4">
							<p className="text-xs font-bold text-stone-900">
								{
									COMPANY_DASHBOARD_PAGE_DATA.mapaCalor
										.resourceBlockTitle
								}
							</p>
							<div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-5">
								{(
									[
										[
											'favorable',
											COMPANY_DASHBOARD_PAGE_DATA
												.mapaCalor.legendFavorable,
										],
										[
											'attention',
											COMPANY_DASHBOARD_PAGE_DATA
												.mapaCalor.legendAttention,
										],
										[
											'critical',
											COMPANY_DASHBOARD_PAGE_DATA
												.mapaCalor.legendCritical,
										],
									] as const
								).map(([key, name]) => (
									<span
										key={key}
										className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-stone-800"
									>
										<span
											className={cn(
												'h-2.5 w-8 shrink-0 rounded border',
												heatLegendResource[key]
													.swatchClass,
											)}
											aria-hidden
										/>
										<span>{name}</span>
										<span className="font-semibold normal-case tabular-nums text-stone-600">
											{heatLegendResource[key].range}
										</span>
									</span>
								))}
							</div>
							<div className="mt-3 flex items-center justify-end">
								<div className="w-full max-w-[16rem] sm:w-[16rem]">
									<Select
										value={resourceSortMode}
										onValueChange={(v) =>
											setResourceSortMode(
												v as HeatMapSortMode,
											)
										}
									>
										<SelectTrigger
											className={selectTriggerClass}
										>
											<SelectValue>
												{
													HEAT_MAP_SORT_LABELS[
														resourceSortMode
													]
												}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											<SelectGroup>
												<SelectItem value="criticalFirst">
													Ordenação: críticos primeiro
												</SelectItem>
												<SelectItem value="favorableFirst">
													Ordenação: favoráveis
													primeiro
												</SelectItem>
											</SelectGroup>
										</SelectContent>
									</Select>
								</div>
							</div>
							<div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
								{resourceRows.map(({ dim, raw, band }) => (
									<div
										key={dim.id}
										className={cn(
											'relative flex min-h-[4.5rem] flex-col justify-between rounded-xl border p-2.5 text-left',
											copsoqHeatCellClass(band),
										)}
									>
										<span className="absolute right-2 top-2 shrink-0">
											<Tooltip
												position="top"
												tapToPinOnTouch
												content={`Esta subcategoria pertence a: ${dim.groupLabel}.`}
												className="max-w-72 whitespace-normal border border-stone-200 bg-white text-[10px] font-medium leading-snug text-stone-700 shadow-md"
											>
												<button
													type="button"
													className="rounded-full p-0.5 text-stone-500 transition-colors hover:bg-stone-200/60 hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-300"
													aria-label={`Origem da subcategoria ${dim.shortLabel}`}
												>
													<Info className="size-3" />
												</button>
											</Tooltip>
										</span>
										<span className="text-lg font-black tabular-nums leading-none">
											{raw == null
												? '—'
												: `${
														Math.round(raw * 10) /
														10
												  }/5`}
										</span>
										<span className="mt-0.5 pr-4 text-[10px] font-bold leading-tight">
											{dim.shortLabel}
										</span>
									</div>
								))}
							</div>
						</div>
					) : null}
				</div>
			</div>
		</section>
	);
};

export { CompanyDashboardPage };
